/**
 * Unolet Base JavaScript
 * ------------------------
 * Core functions and general configurations for the Unolet project.
 *
 * @version     1.0.0
 * @author      Unolet Development Team
 * @contact     dev@unolet.com
 * @website     https://www.unolet.com
 * @copyright   © 2025 Unolet
 * @license     All rights reserved. Internal use only.
 */


import axios from 'axios';
import * as bootstrap from 'bootstrap';
import $ from 'jquery';


const non_field_errors = 'non_field_errors';


// Espera a que el DOM esté completamente cargado antes de inicializar algunos componentes
document.addEventListener('DOMContentLoaded', function () {

  // Inicializa los tooltips y popovers de Bootstrap
  const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"], [data-bs-toggle="tooltip"]'));
  popoverTriggerList.map(function (popoverTriggerEl) {
    return new bootstrap.Popover(popoverTriggerEl);
  });

  /**
   * Desactiva todos los botones de envío asociados a un formulario cuando este se envía,
   * incluyendo botones internos y externos que usan el atributo [form="..."].
   * Esto previene envíos múltiples accidentales al hacer clic repetidamente.
   * @example
   * <form id="myForm">
   *   <!-- Estos botones internos en el formulario se desactivarán al enviar el formulario. -->
   *   <button type="submit">Enviar</button>
   * </form>
   * <!-- Este botón externo también se desactivará al enviar el formulario -->
   * <button type="submit" form="myForm">Enviar Externo</button>
   */

  // Agrega un listener a todos los botones submit para registrar el botón presionado
  let lastClickedButton: HTMLButtonElement | HTMLInputElement | null = null;

  document.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
    'button[type="submit"], input[type="submit"]'
  ).forEach(btn => {
    btn.addEventListener('click', () => {
      lastClickedButton = btn;
    });
  });

  const forms = document.querySelectorAll<HTMLFormElement>('form');
  forms.forEach(form => {
    form.addEventListener('submit', () => {
      // Si el botón tiene name/value, crear un input hidden
      if (
        lastClickedButton &&
        lastClickedButton.name &&
        (lastClickedButton.form === form || lastClickedButton.getAttribute("form") === form.id)
      ) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = lastClickedButton.name;
        hiddenInput.value = lastClickedButton.value;
        form.appendChild(hiddenInput);
      }

      // Buscar todos los botones que pertenecen a este formulario
      const selector = `button[form="${form.id}"], input[type="submit"][form="${form.id}"]`;
      const externalButtons = document.querySelectorAll<HTMLButtonElement | HTMLInputElement>(selector);

      // También incluir los botones internos del formulario
      const internalButtons = form.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input[type="submit"]');

      // Desactivar todos
      [...externalButtons, ...internalButtons].forEach(btn => {
        btn.disabled = true;
      });
    });
  });

});


/**
 * Permite redimensionar columnas de tablas con la clase '.table-resize'
 */
window.addEventListener("load", () => {
  let thElm: HTMLTableCellElement | undefined;
  let startOffset = 0;

  document.querySelectorAll<HTMLTableCellElement>(".table-resize th").forEach((th) => {
    th.style.position = "relative";

    const grip = document.createElement("div");
    grip.innerHTML = "&nbsp;";
    Object.assign(grip.style, {
      position: "absolute",
      top: "0",
      right: "0",
      bottom: "0",
      width: "5px",
      cursor: "col-resize",
    } as CSSStyleDeclaration);

    grip.addEventListener("mousedown", (e: MouseEvent) => {
      thElm = th;
      startOffset = th.offsetWidth - e.pageX;
      e.preventDefault(); // Previene selección de texto accidental
    });

    th.appendChild(grip);
  });

  document.addEventListener("mousemove", (e: MouseEvent) => {
    if (thElm) {
      thElm.style.width = `${startOffset + e.pageX}px`;
    }
  });

  document.addEventListener("mouseup", () => {
    thElm = undefined;
  });
});


const popoverInstances: bootstrap.Popover[] = [];

/**
 * Muestra un popover de Bootstrap en el elemento que dispara el evento.
 * Si ya hay un popover activo, lo reutiliza.
 * Esta función se puede usar en componentes Vue.js para mostrar información adicional al pasar el mouse sobre un elemento.
 * @param {MouseEvent} event - Evento del mouse que dispara el popover.
 * @param {string} content - Contenido HTML del popover.
 * @param {string} [placement='right'] - Posición del popover (default: 'right').
 */
function showPopover(event: MouseEvent, content, placement = 'right') {
  // Si ya hay un popover activo para este elemento, no crear otro
  const target = event.target as HTMLElement | null;
  if (target && (target as any)._popover) {
    (target as any)._popover.show();
    return;
  }

  // Crear popover con Bootstrap JS nativo
  const popover = new bootstrap.Popover(event.target, {
    content: content,
    html: true,
    trigger: 'manual',  // manual porque controlamos con eventos mouseenter/mouseleave de Vue
    placement: placement,
  });

  popover.show();

  if (event.target) {
    (event.target as any)._popover = popover;
  }

  // Agregar el popover a la lista de instancias para poder ocultarlo más tarde
  popoverInstances.push(popover);
}


/**
 * Oculta el popover activo en el elemento que dispara el evento.
 * Esta función se puede usar en componentes Vue.js para ocultar el popover al salir del mouse.
 * @param {MouseEvent} event - Evento del mouse que dispara la ocultación del popover.
 */
function hidePopover(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (target && (target as any)._popover) {
    (target as any)._popover.hide();
    // También puedes eliminar la referencia al popover si ya no lo necesitas
    delete (target as any)._popover;
  }

  // Si estás usando popoverInstances, puedes limpiar aquí si es necesario
  const index = popoverInstances.findIndex(p => p._element === target);
  if (index !== -1) {
    popoverInstances.splice(index, 1);
  }
}


/**
 * Configura Axios para manejar solicitudes API con CSRF token.
 * Este objeto se usa para hacer solicitudes a la API de Django con el token CSRF necesario.
 */
const apiAxios = axios.create({
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken'
});
// Intercepta las solicitudes para agregar el token CSRF a los encabezados
apiAxios.interceptors.request.use(config => {
  const csrfToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)csrftoken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
  );
  config.headers['X-CSRFToken'] = csrfToken;

  // Mostrar la URL completa en consola (para debug)
  const fullUrl = config.baseURL
    ? config.baseURL.replace(/\/+$/, '') + '/' + config.url?.replace(/^\/+/, '')
    : config.url;

  console.debug('[API Request]', config.method?.toUpperCase(), fullUrl);

  return config;
});


/**
 * Ordena los elementos de una tabla por un campo específico.
 * @param field_name
 * @param param_name
 */
function orderBy(
  field_name,
  param_name = "order_by",
) {
  const url = new URL(window.location.href);
  url.searchParams.set(param_name, field_name);
  window.location.href = url.toString();
}


/**
 * Activa la vista previa de una imagen seleccionada desde un `<input type="file">`,
 * actualizando dinámicamente un elemento `<img>` con la imagen seleccionada.
 * @param inputElementId - ID del elemento `<input type="file">`
 * @param imgElementId - ID del elemento `<img>` donde se mostrará la vista previa
 * @param eventName - Evento que dispara la vista previa (por defecto: 'change')
 *
 * El `<img>` debe tener un atributo `data-default-src` con la URL de la imagen por defecto.
 *
 * @example
 * <!-- HTML -->
 * <input type="file" id="photoInput" />
 * <img id="photoPreview" data-default-src="/static/default.jpg" />
 *
 * // TypeScript
 * activateImagePreview('photoInput', 'photoPreview');
 */
function activateImagePreview(
  inputElementId: string,
  imgElementId: string,
  eventName: string = 'change'
): void {
  const inputElement = document.getElementById(inputElementId) as HTMLInputElement | null;
  const imgElement = document.getElementById(imgElementId) as HTMLImageElement | null;

  if (!inputElement || !imgElement) {
    console.warn(`Elemento no encontrado: input=${inputElementId}, img=${imgElementId}`);
    return;
  }

  inputElement.addEventListener(eventName, (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    const defaultSrc = imgElement.dataset.defaultSrc || '';

    imgElement.src = file ? URL.createObjectURL(file) : defaultSrc;
  });

  // Establecer imagen por defecto al iniciar
  imgElement.src = imgElement.dataset.defaultSrc || '';
}


/**
 * Activa o desactiva el modo oscuro dinámicamente según la respuesta del servidor.
 * Envía una solicitud al backend que actualiza una variable de sesión indicando el estado del modo oscuro.
 * Luego, aplica el tema correspondiente (`dark` o `light`) al atributo `data-bs-theme` del `<html>`.
 * @example
 * // Enlazar a un botón de modo oscuro
 * document.getElementById('toggleDarkMode')?.addEventListener('click', activateDarkMode);
 */
async function activateDarkMode(): Promise<void> {
  try {
    const res = await apiAxios.post('/conf/setdarkmode/');
    if (res.status === 200) {
      const htmlElement = document.documentElement;
      const mode: 'dark' | 'light' = res.data.darkmode === true ? 'dark' : 'light';
      htmlElement.setAttribute('data-bs-theme', mode);
    }
  } catch (error) {
    console.error('Error al activar modo oscuro:', error);
  }
}


/**
 * Navega hacia una URL específica, con opción de abrir en una nueva pestaña.
 * Ignora la acción si el usuario tiene texto seleccionado (para evitar navegación accidental).
 * @param url - URL a la que se desea navegar.
 * @param newTab - Si es `true`, abre la URL en una nueva pestaña. Por defecto es `false`.
 * @example
 * // Redirige a "/dashboard"
 * goTo('/dashboard');
 *
 * // Abre una URL externa en una nueva pestaña
 * goTo('https://example.com', true);
 */
function goTo(url: string, newTab: boolean = false): void {
  if (window.getSelection()?.toString()) {
    return;
  }

  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}


/**
 * Elimina una orden de venta.
 * Si la orden tiene un ID, se elimina del backend; si no, se elimina del array local.
 * @param {Object} order - Objeto de la orden a eliminar.
 */
function textTruncate(str: string, length: number = 100): string {
  if (typeof str !== 'string') {
    return '';
  }
  if (str.length <= length) {
    return str;
  }
  return str.slice(0, length) + '...';
}


/**
 * Formatea un número con separadores de miles y una cantidad específica de decimales.
 * @param num - Número a formatear.
 * @param decimalPlaces - Número de decimales a mostrar. Por defecto: 2.
 * @returns String con el número formateado o cadena vacía si no es válido.
 * @example
 * intcomma(1234567.89);        // "1,234,567.89"
 * intcomma(5000);              // "5,000.00"
 * intcomma("invalid");         // ""
 * intcomma(1234567.8, 3);      // "1,234,567.800"
 */
function intcomma(num: number | string, decimalPlaces: number = 2): string {
  let parsed = parseFloat(num as string);
  if (isNaN(parsed)) return "";

  const [integerPart, rawDecimal] = parsed.toString().split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const decimalPart = (rawDecimal || "").padEnd(20, "0").slice(0, decimalPlaces);

  return `${formattedInteger}.${decimalPart}`;
}


/**
 * Tipos de impuesto disponibles.
 */
const FIXED = 'FIXED';
const PERCENTAGE = 'PERCENTAGE';

/**
 * Calcula el impuesto basado en el tipo y valor proporcionado.
 * @param amount - Monto base sobre el cual se calcula el impuesto.
 * @param taxType - Tipo de impuesto: 'PORCENTAJE' o 'FIJO'.
 * @param taxValue - Valor del impuesto (porcentaje o monto fijo).
 * @returns Valor del impuesto calculado.
 * @example
 * calculateTax(1000, 'PORCENTAJE', 18); // 180
 * calculateTax(1000, 'FIJO', 50);       // 50
 */
function calculateTax(
  amount: number,
  taxType: string = PERCENTAGE,
  taxValue: number = 0
): number {
  if (taxType === PERCENTAGE) {
    return (amount * taxValue) / 100;
  }

  if (taxType === FIXED) {
    return taxValue;
  }

  return 0;
}


/**
 * Extrae el monto base antes de impuesto desde un total con impuesto incluido.
 * @param totalAmount - Importe total (incluye impuesto).
 * @param taxType - Tipo de impuesto: 'PERCENTAGE' o 'FIXED'.
 * @param taxValue - Valor del impuesto (porcentaje o monto fijo).
 * @returns Monto base sin impuesto.
 * @example
 * extractTaxBase(1180, 'PERCENTAGE', 18); // 1000
 * extractTaxBase(1050, 'FIXED', 50);       // 1000
 */
function extractTaxBase(
  totalAmount: number = 0,
  taxType: string = PERCENTAGE,
  taxValue: number = 0
): number {
  totalAmount = parseFloat(totalAmount.toString());
  taxValue = parseFloat(taxValue.toString());

  if (taxType === PERCENTAGE) {
    return totalAmount / ((taxValue / 100) + 1);
  }

  if (taxType === FIXED) {
    return totalAmount - taxValue;
  }

  return totalAmount;
}


/**
 * Calcula los totales generales de la orden: subtotal, descuentos, impuestos y total.
 * @param {Object} order - Objeto de orden a procesar.
 * @returns {Object} Orden con totales calculados.
 */
function calculateOrderTotals(order: any): any {
  let subtotal = 0.0;
  let discount_amount = 0.0;
  let tax_amount = 0.0;
  let total = 0.0;

  for (let i = 0; i < order.lines.length; i++) {
    const line = this.calculateLineTotals(order.lines[i], order.is_tax_exempt);
    order.lines[i] = line;

    subtotal += parseFloat(line.subtotal) || 0.0;
    discount_amount += parseFloat(line.discount_amount) || 0.0;
    tax_amount += parseFloat(line.tax_amount) || 0.0;
  }

  total = subtotal - discount_amount + tax_amount;

  order.subtotal = subtotal.toFixed(2);
  order.discount_amount = discount_amount.toFixed(2);
  order.tax_amount = tax_amount.toFixed(2);
  order.total = total.toFixed(2);
  return order;
}


/**
 * Calcula los totales de una factura, reutilizando la lógica de totales de orden.
 * @param {Object} invoice - Objeto de factura a procesar.
 * @returns {Object} Factura con totales calculados.
 */
function calculateInvoiceTotals(invoice: any): any {
  return calculateOrderTotals(invoice);
}


/**
 * Calcula los totales de una nota de crédito, reutilizando la lógica de totales de orden.
 * @param {Object} creditnote - Objeto de nota de crédito a procesar.
 * @returns {Object} Nota de crédito con totales calculados.
 */
function calculateCreditNoteTotals(creditnote: any): any {
  return calculateOrderTotals(creditnote);
}


/**
 * Calcula los totales de una nota de débito, reutilizando la lógica de totales de orden.
 * @param {Object} debitnote - Objeto de nota de débito a procesar.
 * @returns {Object} Nota de débito con totales calculados.
 */
function calculateDebitNoteTotals(debitnote: any): any {
  return calculateOrderTotals(debitnote);
}


/**
 * Calcula los totales de una línea de orden: subtotal, descuento, impuestos y total.
 * @param {Object} line - Objeto de línea de orden a procesar.
 * @returns {Object} Línea con totales calculados.
 * @param {boolean} is_tax_exempt - Indica si la línea está exenta de impuestos (default: false).
 */
function calculateLineTotals(line: any, is_tax_exempt?: boolean): any {
  const price = parseFloat(line.price) || 0.0;
  const quantity = parseFloat(line.quantity) || 1;
  const discount_percentage = parseFloat(line.discount_percentage) || 0.0;

  const subtotal = price * quantity;
  const discount_amount = (subtotal * discount_percentage) / 100;

  let tax_amount = 0.0;

  if (!is_tax_exempt && line.product && line.product.taxes && line.product.taxes.length > 0) {
    for (const tax of line.product.taxes) {
      const taxRate = parseFloat(tax.rate) || 0.0;
      tax_amount += (subtotal - discount_amount) * (taxRate / 100);
    }
  }

  const total = subtotal - discount_amount + tax_amount;

  line.subtotal = subtotal.toFixed(2);
  line.discount_amount = discount_amount.toFixed(2);
  line.tax_amount = tax_amount.toFixed(2);
  line.total = total.toFixed(2);
  return line;
}


/**
 * Analiza los errores de validación devueltos por el backend y los organiza en un objeto.
 * - Maneja errores generales (non_field_errors).
 * - Maneja errores específicos de campos.
 * - Maneja errores de '__all__' si existen.
 * @param errors - Objeto con los errores de validación devueltos por el backend.
 * @returns Objeto con los errores organizados por campo.
 *
 * @example
 * const errors = {
 *   non_field_errors: "Error general",
 *   field1: "Error en el campo 1",
 *   field2: ["Error en el campo 2.1", "Error en el campo 2.2"],
 *   __all__: "Error global"
 * };
 * const parsedErrors = parseValidationErrors(errors);
 * // parsedErrors será:
 * {
 *   non_field_errors: ["Error general"],
 *   field1: ["Error en el campo 1"],
 *   field2: ["Error en el campo 2.1", "Error en el campo 2.2"],
 *   __all__: ["Error global"]
 * }
 */
function parseValidationErrors(errors: Record<string, any>): Record<string, string[]> {
  const parsedErrors: Record<string, string[]> = {};

  if (!errors || typeof errors !== 'object') {
    console.warn(`parseValidationErrors: ${typeof errors} - ${errors}`);
    return parsedErrors;
  }

  // Maneja errores generales (non_field_errors) tipico de Rest Framework
  if (errors.non_field_errors) {
    parsedErrors[non_field_errors] = Array.isArray(errors.non_field_errors)
      ? errors.non_field_errors
      : [errors.non_field_errors];
  }

  // Maneja errores globales (__all__) tipico de Django
  if (errors.__all__) {
    parsedErrors[non_field_errors] = Array.isArray(errors.__all__)
      ? errors.__all__
      : [errors.__all__];
  }

  // Maneja errores específicos de campos
  for (const field in errors) {
    if (field !== non_field_errors) {
      parsedErrors[field] = Array.isArray(errors[field])
        ? errors[field]
        : [errors[field]];
    }
  }

  return parsedErrors;
}


/**
 * Opciones para la función `notify`, que define los parámetros de la notificación.
 */
interface NotifyOptions {
  title: string;
  body?: string;
  icon?: string;
  audio?: string;
  action?: () => void;
  timeout?: number;
}


/**
 * Opciones extendidas para la función `notifyMe`, que incluye un campo `force` para forzar la solicitud de permisos.
 */
interface NotifyMeOptions extends NotifyOptions {
  force?: boolean;
}


/**
 * Muestra una notificación de escritorio con sonido opcional y acción al hacer clic.
 * @param options - Opciones de la notificación
 */
function notify({
  title,
  body = '',
  icon = '/static/img/base/logo.png',
  audio = '/static/audio/tap-notification.mp3',
  action,
  timeout = 5000,
}: NotifyOptions): void {
  if (!title && !body) {
    console.log("No se indicó un título o cuerpo para la notificación.");
    return;
  }

  const notification = new Notification(title, { body, icon });

  if (audio) {
    const audioObj = new Audio(audio);
    audioObj.play().catch((err) => console.warn('Error reproduciendo audio:', err));
  }

  if (typeof action === 'function') {
    notification.onclick = () => action();
  }

  setTimeout(() => notification.close(), timeout);
}


/**
 * Solicita permiso para mostrar notificaciones del navegador y, si se concede, muestra una notificación
 * con soporte para ícono, sonido y acción personalizada al hacer clic.
 * - Si el navegador ya tiene permiso, muestra la notificación directamente.
 * - Si el permiso fue denegado y `force` es `true`, vuelve a solicitar permiso.
 * - Requiere que la página esté servida por HTTPS (excepto en `localhost`).
 * @param options - Objeto con las opciones de la notificación:
 *   - `title`: Título de la notificación (obligatorio)
 *   - `body`: Cuerpo del mensaje
 *   - `icon`: Ruta al ícono de la notificación (por defecto: `/static/img/base/logo.png`)
 *   - `audio`: Ruta al sonido de notificación (por defecto: `/static/audio/tap-notification.mp3`)
 *   - `action`: Función que se ejecuta al hacer clic en la notificación
 *   - `timeout`: Tiempo en milisegundos antes de cerrar la notificación (por defecto: `5000`)
 *   - `force`: Fuerza la solicitud de permisos aunque hayan sido denegados previamente
 * @example
 * notifyMe({
 *   title: 'Nuevo mensaje',
 *   body: 'Has recibido un mensaje nuevo.',
 *   action: () => window.open('/inbox'),
 * });
 */
function notifyMe({
  title,
  body = '',
  icon,
  audio,
  action,
  timeout = 5000,
  force = false,
}: NotifyMeOptions): void {
  if (!('Notification' in window)) {
    console.error('Este navegador no soporta notificaciones de escritorio.');
    return;
  }

  if (Notification.permission === 'granted') {
    notify({ title, body, icon, audio, action, timeout });
  } else if (Notification.permission !== 'denied' || force) {
    Notification.requestPermission((permission) => {
      if (permission === 'granted') {
        const msgTitle = (window as any).UNOLET_I18N?.notifEnabledTitle || '¡Notificaciones activadas!';
        const msgBody = (window as any).UNOLET_I18N?.notifEnabledBody || 'Ahora recibirás notificaciones de Unolet.';
        notify({ title: msgTitle, body: msgBody });
        notify({ title, body, icon, audio, action, timeout });
      } else {
        alert((window as any).UNOLET_I18N?.notifDenied || 'Las notificaciones están desactivadas.');
      }
    });
  }
}


// Exponer funciones globalmente
(window as any).FIXED = FIXED;
(window as any).PERCENTAGE = PERCENTAGE;
(window as any).$ = $;
(window as any).jQuery = $;
(window as any).bootstrap = bootstrap;
(window as any).apiAxios = apiAxios;
(window as any).activateDarkMode = activateDarkMode;
(window as any).activateImagePreview = activateImagePreview;
(window as any).calculateOrderTotals = calculateOrderTotals;
(window as any).calculateInvoiceTotals = calculateInvoiceTotals;
(window as any).calculateCreditNoteTotals = calculateCreditNoteTotals;
(window as any).calculateDebitNoteTotals = calculateDebitNoteTotals;
(window as any).calculateLineTotals = calculateLineTotals;
(window as any).calculateTax = calculateTax;
(window as any).extractTaxBase = extractTaxBase;
(window as any).goTo = goTo;
(window as any).intcomma = intcomma;
(window as any).textTruncate = textTruncate;
(window as any).notify = notify;
(window as any).notifyMe = notifyMe;
(window as any).orderBy = orderBy;
(window as any).showPopover = showPopover;
(window as any).hidePopover = hidePopover;
(window as any).parseValidationErrors = parseValidationErrors;

const utils = {
  activateDarkMode,
  activateImagePreview,
  calculateOrderTotals,
  calculateInvoiceTotals,
  calculateCreditNoteTotals,
  calculateDebitNoteTotals,
  calculateLineTotals,
  calculateTax,
  extractTaxBase,
  goTo,
  textTruncate,
  intcomma,
  notify,
  notifyMe,
  orderBy,
  showPopover,
  hidePopover,
  parseValidationErrors,
};


// Exportar los objetos para uso en otros módulos
export default apiAxios;
export {
  utils,
};